import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';

// Styled Components
const ReviewContainer = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.text};
  margin-bottom: 20px;
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: ${props => props.theme.background};
  padding: 15px;
  border-radius: 6px;
  text-align: center;
  border: 1px solid ${props => props.theme.border};
`;

const StatNumber = styled.div`
  font-size: 1.8em;
  font-weight: bold;
  color: ${props => props.theme.primary};
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: ${props => props.theme.subText};
  font-size: 0.9em;
`;

const SuggestionCard = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
`;

const SuggestionHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: flex-start;
  margin-bottom: 15px;
  gap: 15px;
`;

const ExpertInfo = styled.div`
  flex: 1;
`;

const ExpertName = styled.h3`
  color: ${props => props.theme.text};
  margin: 0 0 5px 0;
  font-size: 1.1em;
`;

const PredictionInfo = styled.div`
  color: ${props => props.theme.subText};
  font-size: 0.9em;
  margin-bottom: 10px;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: bold;
  background: ${props => {
    if (props.status === 'success') return '#4CAF5020';
    if (props.status === 'miss') return '#f4433620';
    return '#ff980020';
  }};
  color: ${props => {
    if (props.status === 'success') return '#4CAF50';
    if (props.status === 'miss') return '#f44336';
    return '#ff9800';
  }};
  border: 1px solid ${props => {
    if (props.status === 'success') return '#4CAF50';
    if (props.status === 'miss') return '#f44336';
    return '#ff9800';
  }};
`;

const StatusChange = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
`;

const Arrow = styled.span`
  color: ${props => props.theme.primary};
  font-weight: bold;
  font-size: 1.2em;
`;

const ReasonText = styled.div`
  color: ${props => props.theme.text};
  background: ${props => props.theme.surface};
  padding: 10px;
  border-radius: 4px;
  margin: 10px 0;
  font-size: 0.9em;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
`;

const ActionButton = styled.button<{ variant: 'approve' | 'reject' | 'details' }>`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  
  background: ${props => {
    if (props.variant === 'approve') return '#4CAF50';
    if (props.variant === 'reject') return '#f44336';
    return props.theme.border;
  }};
  
  color: ${props => {
    if (props.variant === 'details') return props.theme.text;
    return 'white';
  }};

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: ${props => props.theme.subText};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${props => props.theme.subText};
`;

const RefreshButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-bottom: 20px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const DetailsModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.h3`
  color: ${props => props.theme.text};
  margin: 0 0 20px 0;
`;

const ModalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid ${props => props.theme.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ModalLabel = styled.span`
  color: ${props => props.theme.subText};
  font-weight: bold;
`;

const ModalValue = styled.span`
  color: ${props => props.theme.text};
  text-align: right;
  max-width: 60%;
  word-break: break-word;
`;

// Types
interface PredictionSuggestion {
  id: number;
  expert_name: string;
  prediction_type: string;
  price_prediction: string;
  current_status: string;
  suggested_status: string;
  reason: string;
  confidence: string;
  source_url?: string;
  start_date: string;
  end_date: string;
  has_expired: boolean;
}

interface PredictionReviewProps {
  theme: any;
}

const PredictionReview: React.FC<PredictionReviewProps> = ({ theme }) => {
  const [suggestions, setSuggestions] = useState<PredictionSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [totalPredictions, setTotalPredictions] = useState<number>(0);
  const [selectedPrediction, setSelectedPrediction] = useState<PredictionSuggestion | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);

  const loadSuggestions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiBase}/api/admin/prediction-review-suggestions`);
      
      if (response.data.success) {
        setSuggestions(response.data.suggestions);
        setCurrentPrice(response.data.current_price);
        setTotalPredictions(response.data.total_predictions);
      }
    } catch (error) {
      console.error('Error loading prediction suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuggestions();
  }, []);

  const applyStatusChange = async (suggestionId: number, newStatus: string) => {
    try {
      setProcessingId(suggestionId);
      
      const response = await axios.post(
        `${apiBase}/api/admin/prediction-review-suggestions/${suggestionId}/apply`,
        { new_status: newStatus }
      );
      
      if (response.data.success) {
        // Remove the applied suggestion from the list
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      }
    } catch (error) {
      console.error('Error applying status change:', error);
      alert('Failed to apply status change: ' + (error as any).response?.data?.message || error);
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    if (status === 'success') return '✅';
    if (status === 'miss') return '❌';
    return '⏳';
  };

  const getConfidenceColor = (confidence: string) => {
    if (confidence === 'high') return '#4CAF50';
    if (confidence === 'medium') return '#ff9800';
    return '#f44336';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <ReviewContainer theme={theme}>
        <LoadingSpinner theme={theme}>
          🔄 Loading prediction suggestions...
        </LoadingSpinner>
      </ReviewContainer>
    );
  }

  return (
    <>
      <ReviewContainer theme={theme}>
        <SectionTitle theme={theme}>
          🔍 Prediction Status Review
          <RefreshButton theme={theme} onClick={loadSuggestions}>
            🔄 Refresh
          </RefreshButton>
        </SectionTitle>

        <StatsRow>
          <StatCard theme={theme}>
            <StatNumber theme={theme}>{formatPrice(currentPrice)}</StatNumber>
            <StatLabel theme={theme}>Current BTC Price</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatNumber theme={theme}>{totalPredictions}</StatNumber>
            <StatLabel theme={theme}>Total Predictions</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatNumber theme={theme}>{suggestions.length}</StatNumber>
            <StatLabel theme={theme}>Need Review</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatNumber theme={theme}>
              {suggestions.filter(s => s.confidence === 'high').length}
            </StatNumber>
            <StatLabel theme={theme}>High Confidence</StatLabel>
          </StatCard>
        </StatsRow>

        {suggestions.length === 0 ? (
          <EmptyState theme={theme}>
            🎉 All predictions are up to date! No reviews needed.
          </EmptyState>
        ) : (
          suggestions.map((suggestion) => (
            <SuggestionCard key={suggestion.id} theme={theme}>
              <SuggestionHeader>
                <ExpertInfo>
                  <ExpertName theme={theme}>
                    {suggestion.expert_name}
                  </ExpertName>
                  <PredictionInfo theme={theme}>
                    {suggestion.prediction_type.toUpperCase()}: {suggestion.price_prediction}
                  </PredictionInfo>
                  <PredictionInfo theme={theme}>
                    Period: {formatDate(suggestion.start_date)} → {formatDate(suggestion.end_date)}
                    {suggestion.has_expired && ' (EXPIRED)'}
                  </PredictionInfo>
                </ExpertInfo>
                
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    color: getConfidenceColor(suggestion.confidence),
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    marginBottom: '5px'
                  }}>
                    {suggestion.confidence.toUpperCase()} CONFIDENCE
                  </div>
                </div>
              </SuggestionHeader>

              <StatusChange>
                <StatusBadge status={suggestion.current_status}>
                  {getStatusIcon(suggestion.current_status)} {suggestion.current_status.toUpperCase()}
                </StatusBadge>
                <Arrow theme={theme}>→</Arrow>
                <StatusBadge status={suggestion.suggested_status}>
                  {getStatusIcon(suggestion.suggested_status)} {suggestion.suggested_status.toUpperCase()}
                </StatusBadge>
              </StatusChange>

              <ReasonText theme={theme}>
                <strong>Reason:</strong> {suggestion.reason}
              </ReasonText>

              <ActionButtons>
                <ActionButton
                  variant="approve"
                  onClick={() => applyStatusChange(suggestion.id, suggestion.suggested_status)}
                  disabled={processingId === suggestion.id}
                >
                  {processingId === suggestion.id ? '⏳ Applying...' : '✅ Apply Change'}
                </ActionButton>
                
                <ActionButton
                  variant="reject"
                  onClick={() => setSuggestions(prev => prev.filter(s => s.id !== suggestion.id))}
                  disabled={processingId === suggestion.id}
                >
                  ❌ Dismiss
                </ActionButton>
                
                <ActionButton
                  variant="details"
                  onClick={() => setSelectedPrediction(suggestion)}
                  theme={theme}
                >
                  📋 Details
                </ActionButton>
              </ActionButtons>
            </SuggestionCard>
          ))
        )}
      </ReviewContainer>

      {/* Details Modal */}
      <DetailsModal isOpen={!!selectedPrediction}>
        <ModalContent theme={theme}>
          {selectedPrediction && (
            <>
              <ModalHeader theme={theme}>
                Prediction Details - {selectedPrediction.expert_name}
              </ModalHeader>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>ID:</ModalLabel>
                <ModalValue theme={theme}>{selectedPrediction.id}</ModalValue>
              </ModalRow>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>Expert:</ModalLabel>
                <ModalValue theme={theme}>{selectedPrediction.expert_name}</ModalValue>
              </ModalRow>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>Type:</ModalLabel>
                <ModalValue theme={theme}>{selectedPrediction.prediction_type}</ModalValue>
              </ModalRow>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>Prediction:</ModalLabel>
                <ModalValue theme={theme}>{selectedPrediction.price_prediction}</ModalValue>
              </ModalRow>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>Current Status:</ModalLabel>
                <ModalValue theme={theme}>
                  <StatusBadge status={selectedPrediction.current_status}>
                    {selectedPrediction.current_status}
                  </StatusBadge>
                </ModalValue>
              </ModalRow>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>Suggested Status:</ModalLabel>
                <ModalValue theme={theme}>
                  <StatusBadge status={selectedPrediction.suggested_status}>
                    {selectedPrediction.suggested_status}
                  </StatusBadge>
                </ModalValue>
              </ModalRow>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>Start Date:</ModalLabel>
                <ModalValue theme={theme}>{formatDate(selectedPrediction.start_date)}</ModalValue>
              </ModalRow>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>End Date:</ModalLabel>
                <ModalValue theme={theme}>{formatDate(selectedPrediction.end_date)}</ModalValue>
              </ModalRow>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>Has Expired:</ModalLabel>
                <ModalValue theme={theme}>{selectedPrediction.has_expired ? 'Yes' : 'No'}</ModalValue>
              </ModalRow>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>Confidence:</ModalLabel>
                <ModalValue theme={theme}>
                  <span style={{ color: getConfidenceColor(selectedPrediction.confidence) }}>
                    {selectedPrediction.confidence.toUpperCase()}
                  </span>
                </ModalValue>
              </ModalRow>
              
              <ModalRow theme={theme}>
                <ModalLabel theme={theme}>Reason:</ModalLabel>
                <ModalValue theme={theme}>{selectedPrediction.reason}</ModalValue>
              </ModalRow>
              
              {selectedPrediction.source_url && (
                <ModalRow theme={theme}>
                  <ModalLabel theme={theme}>Source:</ModalLabel>
                  <ModalValue theme={theme}>
                    <a 
                      href={selectedPrediction.source_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: theme.primary }}
                    >
                      View Original
                    </a>
                  </ModalValue>
                </ModalRow>
              )}
              
              <ActionButtons style={{ marginTop: '20px', justifyContent: 'flex-end' }}>
                <ActionButton
                  variant="details"
                  theme={theme}
                  onClick={() => setSelectedPrediction(null)}
                >
                  Close
                </ActionButton>
              </ActionButtons>
            </>
          )}
        </ModalContent>
      </DetailsModal>
    </>
  );
};

export default PredictionReview;